import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useAuth } from '@/auth/AuthProvider'
import { accessApi, documentsApi, foldersApi, jobsApi, templatesApi, uploadLinksApi } from '@/lib/api'
import type { Folder } from '@/lib/api/folders'
import { isJobActive } from '@/lib/api/jobs'
import { isUploadLinkActive } from '@/lib/api/uploadLinks'
import {
  pageByNumber,
  pageDisplayName,
  type DocumentCounts,
  type FieldType,
  type Job,
  type SinglePage,
  type Template,
  type TemplateAccess,
  type TemplateAccessEntry,
  type UploadLink,
} from '@/types/domain'

/**
 * The cockpit's shared server state: one react-query cache for the template,
 * its document counts, its jobs and its access list, read once by
 * `CockpitLayout` and handed to the four tabs through `CockpitProvider`.
 *
 * Why one hook rather than a query per tab: switching from Dokumente to
 * Vorlage and back must not refetch a template that has not changed, and the
 * four tabs disagreeing about how often to poll is how a gateway acquires a
 * load problem nobody can explain. The keys below are shared with the rest of
 * the app on purpose — `documents/counts` is the same cache entry the
 * Übersicht's card counts use, so a form opened from a card paints its donut
 * from what is already in hand.
 *
 * What is NOT here: the document *list*. It is paged, filtered and selected
 * per tab, it is the one thing that genuinely differs between screens, and
 * caching every filter combination under this hook would make it the opposite
 * of small. The Dokumente tab owns `cockpitKeys.documents` and takes its poll
 * interval from `pollIntervals.documents` below, so the cadence still lives in
 * one place.
 */

// -- Cache keys ------------------------------------------------------------

/**
 * Every key this wave uses. Written down because five agents build against
 * them: a mutation that invalidates `['template', id]` while a screen reads
 * `['templates', id]` is a bug that looks like a caching mystery.
 */
export const cockpitKeys = {
  /** One template, as `GET /v1/templates/:id` returns it. */
  template: (templateId: string) => ['template', templateId] as const,
  /** Shared with the Übersicht's per-card counts (`useTemplateCounts`). */
  counts: (templateId: string) => ['documents', 'counts', templateId] as const,
  /** This template's jobs. Namespaced under `template` so it cannot collide
   *  with the admin build's global `['jobs', filters]`. */
  jobs: (templateId: string) => ['jobs', 'template', templateId] as const,
  access: (templateId: string) => ['access', templateId] as const,
  uploadLinks: (templateId: string) => ['uploadLinks', templateId] as const,
  /** Owned by the Dokumente tab; `view` is its filter/page/search tuple. */
  documents: (templateId: string, view?: unknown) =>
    ['documents', 'list', templateId, view] as const,
  /** The folder listing, shared with Projekte (`FOLDERS_KEY`). */
  folders: ['folders'] as const,
} as const

// -- The polling policy ----------------------------------------------------

/**
 * How often the cockpit asks, and — the part the Flutter cockpit got wrong —
 * when it stops asking.
 *
 * Flutter polls the counts every 15 s for as long as the screen is open, so a
 * finished template left open in a tab over a weekend is 4 requests a minute
 * about numbers that cannot move. Here every loop is off unless something is
 * actually running, and even then it gives up rather than hammering forever.
 *
 * Three phases:
 *   - `idle`    nothing is running. No polling at all; react-query's
 *               refetch-on-focus and the mutations' invalidations cover it.
 *   - `busy`    something is running: the Flutter cadence, which is tuned to
 *               how fast the worker actually finishes a page.
 *   - `slow`    running, but nothing has changed for `stallAfterMs`. A worker
 *               that has produced nothing in five minutes will not be noticed
 *               sooner by asking every three seconds.
 *   - `stopped` still nothing after `giveUpAfterMs`. The screen offers a
 *               manual refresh instead; something is wrong that polling will
 *               not fix.
 *
 * A hidden or unfocused tab polls nothing either way: react-query's
 * `refetchIntervalInBackground` defaults to false, so the overnight tab costs
 * the gateway nothing even mid-analysis.
 */
export const POLL = {
  jobsBusyMs: 3_000,
  documentsBusyMs: 5_000,
  countsBusyMs: 15_000,
  slowMs: 30_000,
  countsSlowMs: 60_000,
  stallAfterMs: 5 * 60_000,
  giveUpAfterMs: 30 * 60_000,
} as const

export type PollPhase = 'idle' | 'busy' | 'slow' | 'stopped'

export interface PollIntervals {
  /** For the Dokumente tab's own list query. */
  documents: number | false
  jobs: number | false
  counts: number | false
}

/**
 * Whether anything is in flight.
 *
 * Deliberately **not** "there are `neu` documents": a document stays `neu`
 * until somebody starts an analysis, so counting it as activity would leave
 * every loop running forever on a template with one unanalysed upload — the
 * exact failure this policy exists to prevent. A queued document is covered
 * by its job, which is `pending` and therefore active.
 */
export function cockpitBusy(counts?: DocumentCounts, jobs?: Job[]): boolean {
  if ((counts?.byStatus?.inProgress ?? 0) > 0) return true
  return (jobs ?? []).some(isJobActive)
}

/**
 * A fingerprint of everything that moves while work is running. When it stops
 * changing, the run has stalled and the cadence backs off.
 */
export function activitySignature(counts?: DocumentCounts, jobs?: Job[]): string {
  const byStatus = counts?.byStatus ?? {}
  const documents = Object.keys(byStatus)
    .sort()
    .map((status) => `${status}:${byStatus[status as keyof typeof byStatus] ?? 0}`)
    .join(',')
  const running = (jobs ?? [])
    .filter(isJobActive)
    .map((job) => `${job.id}:${job.status}:${job.progress}`)
    .sort()
    .join(',')
  return `${counts?.total ?? -1}|${documents}|${running}`
}

export function pollPhase(busy: boolean, msSinceChange: number): PollPhase {
  if (!busy) return 'idle'
  if (msSinceChange >= POLL.giveUpAfterMs) return 'stopped'
  if (msSinceChange >= POLL.stallAfterMs) return 'slow'
  return 'busy'
}

export function pollIntervals(phase: PollPhase): PollIntervals {
  switch (phase) {
    case 'busy':
      return {
        documents: POLL.documentsBusyMs,
        jobs: POLL.jobsBusyMs,
        counts: POLL.countsBusyMs,
      }
    case 'slow':
      return { documents: POLL.slowMs, jobs: POLL.slowMs, counts: POLL.countsSlowMs }
    default:
      return { documents: false, jobs: false, counts: false }
  }
}

/**
 * The current phase, re-evaluated on a clock of its own.
 *
 * The clock is the point. The phase was previously computed at render time
 * and left there, on the assumption that the running poll would provide the
 * next render — but react-query re-renders only when the answer *changed*,
 * and a stalled run is precisely the case where every answer is identical.
 * The backoff therefore never fired in the one situation it exists for: a
 * hung worker kept the three-second cadence for as long as the tab stayed
 * open. One timeout per boundary (five minutes, then thirty) fixes that; a
 * ticking interval would be a second thing polling for no reason.
 *
 * `signature` is the fingerprint of everything that moves while work runs;
 * every change to it restarts the clock.
 */
export function usePollPhase(busy: boolean, signature: string): PollPhase {
  const [changedAt, setChangedAt] = useState(() => Date.now())
  const [, setTick] = useState(0)
  const lastSignature = useRef(signature)
  if (lastSignature.current !== signature) {
    lastSignature.current = signature
    // A render-phase update: React re-runs this render with the new value
    // before committing anything — cheaper than an effect, which would paint
    // one frame at the stale phase.
    setChangedAt(Date.now())
  }

  const phase = pollPhase(busy, Date.now() - changedAt)

  useEffect(() => {
    if (phase !== 'busy' && phase !== 'slow') return
    const until = phase === 'busy' ? POLL.stallAfterMs : POLL.giveUpAfterMs
    const remaining = Math.max(until - (Date.now() - changedAt), 0)
    const timer = window.setTimeout(() => setTick((value) => value + 1), remaining + 50)
    return () => {
      window.clearTimeout(timer)
    }
  }, [phase, changedAt])

  return phase
}

// -- Derived template facts ------------------------------------------------

/** The pages that are layouts in their own right, not variants of another. */
export function primaryPages(template?: Template): SinglePage[] {
  return (template?.pageList ?? []).filter((page) => page.variantOf == null)
}

/** The alternative layouts of one primary page, e.g. the 2018 and 2019 forms. */
export function variantPages(template: Template | undefined, page: number): SinglePage[] {
  return (template?.pageList ?? []).filter((candidate) => candidate.variantOf === page)
}

/**
 * What to call the template page a document matched.
 *
 * Page numbers are never compacted — `matched_page`, `variant_of` and the
 * classifier's cache key all reference them — so a deleted page leaves a gap
 * and a page number is *not* an index into `pageList`. Every lookup in this
 * wave goes through `pageByNumber`/`pageIndexByNumber` for that reason.
 */
export function layoutLabel(template: Template | undefined, page?: number | null): string {
  if (template == null || page == null) return '–'
  const match = pageByNumber(template, page)
  return match ? pageDisplayName(match) : `Seite ${page}`
}

export interface FieldTypeCounts {
  text: number
  checkbox: number
  signature: number
  total: number
}

function bucketFor(type: FieldType): keyof Omit<FieldTypeCounts, 'total'> | null {
  if (type === 'textDetection' || type === 'text') return 'text'
  if (type === 'checkBox' || type === 'radioBox') return 'checkbox'
  if (type === 'signature') return 'signature'
  return null
}

/**
 * The Text / Checkbox / Signatur tile.
 *
 * Counted over the template's **shared fields** where it has them, because
 * those are what becomes a column in the export — a field placed on both the
 * 2018 and the 2019 layout is one field, not two. Older and Firestore
 * templates carry no `fields`, so those fall back to the boxes of the primary
 * pages, deduplicated by `fieldId` where present and by label otherwise.
 */
export function fieldTypeCounts(template?: Template): FieldTypeCounts {
  const counts: FieldTypeCounts = { text: 0, checkbox: 0, signature: 0, total: 0 }
  const add = (type: FieldType) => {
    const bucket = bucketFor(type)
    if (!bucket) return
    counts[bucket] += 1
    counts.total += 1
  }

  if (template?.fields?.length) {
    template.fields.forEach((field) => add(field.type))
    return counts
  }

  const seen = new Set<string>()
  for (const page of template?.pageList ?? []) {
    for (const box of page.boxConfiguration ?? []) {
      const key = box.fieldId ?? `${box.label}|${box.type}`
      if (seen.has(key)) continue
      seen.add(key)
      add(box.type)
    }
  }
  return counts
}

/** Initials for the avatar stack, from a display name or an e-mail address. */
export function initialsFor(entry: { name?: string; email?: string }): string {
  const source = (entry.name ?? '').trim() || (entry.email ?? '').split('@')[0] || ''
  const parts = source.split(/[\s._-]+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/**
 * Whether the signed-in person may change this template.
 *
 * Read off the template's own `accessControl`, which every `GET
 * /v1/templates/:id` already carries — no second request, and it matches what
 * the gateway's `writeMW` checks. When it cannot be decided (a template that
 * reports no access control at all), the answer is *yes*: the gateway is the
 * authority and answers 403, and hiding a control on a guess would leave an
 * owner unable to rename their own form.
 */
export function canWriteTemplate(
  template: Template | undefined,
  userId: string | undefined,
  isSystemAdmin = false,
): boolean {
  if (isSystemAdmin) return true
  if (!template) return false
  const acl = template.accessControl
  if (!acl) return true
  if (!acl.owner && !acl.write?.length && !acl.admin?.length && !acl.read?.length) return true
  if (!userId) return false
  return acl.owner === userId || !!acl.write?.includes(userId) || !!acl.admin?.includes(userId)
}

// -- The dialog convention -------------------------------------------------

/**
 * Dialogs live in `?dialog=…` rather than in component state, so a header
 * button is linkable, survives a reload and can be opened from the app
 * shell's "+" (which sets `?dialog=upload`). `params` carries the rest of the
 * query untouched, so a filter or a page number is not lost by opening a
 * dialog.
 */
export type CockpitDialog = 'upload' | 'share' | (string & {})

export function useCockpitDialog() {
  const [params, setParams] = useSearchParams()
  const dialog = params.get('dialog')

  const open = useCallback(
    (name: CockpitDialog) => {
      const next = new URLSearchParams(params)
      next.set('dialog', name)
      // `replace` on both halves: `close` rewrites the URL back to the
      // screen, so an entry pushed on open would be an entry identical to
      // where the person already is — press Back after three open/close
      // cycles and nothing visible happens for the first three presses.
      setParams(next, { replace: true })
    },
    [params, setParams],
  )

  const close = useCallback(() => {
    const next = new URLSearchParams(params)
    next.delete('dialog')
    setParams(next, { replace: true })
  }, [params, setParams])

  return { dialog, open, close }
}

// -- The hook --------------------------------------------------------------

export interface CockpitState {
  templateId: string
  templateQuery: UseQueryResult<Template>
  countsQuery: UseQueryResult<DocumentCounts>
  jobsQuery: UseQueryResult<Job[]>
  accessQuery: UseQueryResult<TemplateAccess>
  template?: Template
  counts?: DocumentCounts
  jobs: Job[]
  /** Pending or processing, newest first — the "Analyse läuft" card's input. */
  activeJobs: Job[]
  access?: TemplateAccess
  /** Everyone with access apart from the signed-in person. */
  sharedWith: TemplateAccessEntry[]
  folder?: Folder
  /** The breadcrumb's middle crumb. "Ohne Projekt" when the form has none. */
  folderName: string
  canWrite: boolean
  canManage: boolean
  fields: FieldTypeCounts
  layouts: SinglePage[]
  busy: boolean
  phase: PollPhase
  intervals: PollIntervals
  /** True while the first load of the template is in flight. */
  isLoading: boolean
  error: unknown
  /** Refetches everything this hook owns; the tabs' own lists invalidate too. */
  refresh: () => Promise<void>
  /** Targeted invalidation after a mutation, so a tab need not know the keys. */
  invalidate: {
    template: () => Promise<void>
    counts: () => Promise<void>
    jobs: () => Promise<void>
    documents: () => Promise<void>
    access: () => Promise<void>
    uploadLinks: () => Promise<void>
  }
  rename: ReturnType<typeof useRenameTemplate>
}

function useRenameTemplate(templateId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (title: string) => templatesApi.rename(templateId, title),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: cockpitKeys.template(templateId) }),
        // The name is on the Übersicht's cards and in the Projekte tree too.
        queryClient.invalidateQueries({ queryKey: ['templates'] }),
      ])
    },
  })
}

export function useCockpit(templateId: string): CockpitState {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  // The cadence is derived from the very data these queries hold, so it is
  // read back through a ref: react-query calls `refetchInterval` after each
  // fetch, by which time the ref carries the phase this render computed.
  const intervalsRef = useRef<PollIntervals>({ documents: false, jobs: false, counts: false })

  const templateQuery = useQuery({
    queryKey: cockpitKeys.template(templateId),
    queryFn: () => templatesApi.get(templateId),
    enabled: templateId.length > 0,
    // A template changes only when somebody edits it, and every editing path
    // in this build invalidates the key. Five minutes keeps a tab switch free.
    staleTime: 5 * 60_000,
  })

  const countsQuery = useQuery({
    queryKey: cockpitKeys.counts(templateId),
    queryFn: () => documentsApi.counts(templateId),
    enabled: templateId.length > 0,
    staleTime: 10_000,
    refetchInterval: () => intervalsRef.current.counts,
  })

  const jobsQuery = useQuery({
    queryKey: cockpitKeys.jobs(templateId),
    queryFn: async () => (await jobsApi.list(templateId, { limit: 20 })).jobs,
    enabled: templateId.length > 0,
    staleTime: 5_000,
    refetchInterval: () => intervalsRef.current.jobs,
  })

  // `readMW`, so a viewer may read it; `canManage` in the answer says whether
  // the share controls do anything. A failure is quiet: the avatar stack is
  // not worth an error strip over the whole workspace.
  const accessQuery = useQuery({
    queryKey: cockpitKeys.access(templateId),
    queryFn: () => accessApi.get(templateId),
    enabled: templateId.length > 0,
    staleTime: 5 * 60_000,
    retry: false,
  })

  const folderId = templateQuery.data?.folder
  const foldersQuery = useQuery({
    queryKey: cockpitKeys.folders,
    queryFn: () => foldersApi.list(),
    enabled: !!folderId,
    staleTime: 5 * 60_000,
    retry: false,
  })

  const counts = countsQuery.data
  const jobs = useMemo(() => jobsQuery.data ?? [], [jobsQuery.data])
  const activeJobs = useMemo(() => jobs.filter(isJobActive), [jobs])
  const busy = cockpitBusy(counts, jobs)

  const phase = usePollPhase(busy, activitySignature(counts, jobs))
  const intervals = pollIntervals(phase)
  intervalsRef.current = intervals

  const invalidate = useMemo(
    () => ({
      template: async () => {
        await queryClient.invalidateQueries({ queryKey: cockpitKeys.template(templateId) })
      },
      counts: async () => {
        await queryClient.invalidateQueries({ queryKey: cockpitKeys.counts(templateId) })
      },
      jobs: async () => {
        await queryClient.invalidateQueries({ queryKey: cockpitKeys.jobs(templateId) })
      },
      documents: async () => {
        await queryClient.invalidateQueries({ queryKey: ['documents', 'list', templateId] })
      },
      access: async () => {
        await queryClient.invalidateQueries({ queryKey: cockpitKeys.access(templateId) })
      },
      uploadLinks: async () => {
        await queryClient.invalidateQueries({ queryKey: cockpitKeys.uploadLinks(templateId) })
      },
    }),
    [queryClient, templateId],
  )

  const refresh = useCallback(async () => {
    await Promise.all([
      invalidate.template(),
      invalidate.counts(),
      invalidate.jobs(),
      invalidate.documents(),
    ])
  }, [invalidate])

  const template = templateQuery.data
  const access = accessQuery.data
  const sharedWith = useMemo(
    () => (access?.entries ?? []).filter((entry) => entry.userId !== user?.id),
    [access, user?.id],
  )
  const folder = foldersQuery.data?.find((candidate) => candidate.id === folderId)

  const rename = useRenameTemplate(templateId)

  return {
    templateId,
    templateQuery,
    countsQuery,
    jobsQuery,
    accessQuery,
    template,
    counts,
    jobs,
    activeJobs,
    access,
    sharedWith,
    folder,
    folderName: folder?.name ?? 'Ohne Projekt',
    canWrite: canWriteTemplate(template, user?.id, user?.role === 'admin'),
    canManage: access?.canManage ?? false,
    fields: fieldTypeCounts(template),
    layouts: primaryPages(template),
    busy,
    phase,
    intervals,
    isLoading: templateQuery.isLoading,
    error: templateQuery.error,
    refresh,
    invalidate,
    rename,
  }
}

/**
 * The upload links, in their own hook because only the Dokumente tab's QR
 * card needs them and listing them is `writeMW` — a viewer gets a 403, which
 * is information ("Sie dürfen keinen Handy-Link erstellen"), not an error to
 * retry. Shares `cockpitKeys.uploadLinks`, so creating one from anywhere
 * refreshes the card.
 */
export function useCockpitUploadLinks(templateId: string, enabled = true) {
  const query = useQuery({
    queryKey: cockpitKeys.uploadLinks(templateId),
    queryFn: () => uploadLinksApi.list(templateId),
    enabled: enabled && templateId.length > 0,
    staleTime: 60_000,
    retry: false,
  })
  const active: UploadLink | undefined = (query.data ?? []).find((link) =>
    isUploadLinkActive(link),
  )
  const forbidden = (query.error as { status?: number } | null)?.status === 403
  return { ...query, links: query.data ?? [], active, forbidden }
}

// -- The context the four tabs read ---------------------------------------

const CockpitContext = createContext<CockpitState | null>(null)

export const CockpitProvider = CockpitContext.Provider

/**
 * What a tab calls. Never `useCockpit` directly: the layout has already
 * loaded it, and a second caller would run a second copy of the polling
 * policy at its own phase.
 */
export function useCockpitContext(): CockpitState {
  const value = useContext(CockpitContext)
  if (!value) throw new Error('useCockpitContext muss innerhalb von CockpitLayout benutzt werden')
  return value
}
